<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import ReportsAPI from 'dashboard/api/reports';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const route = useRoute();
const isLoading = ref(false);
const errorText = ref('');
const totalHandoffConversations = ref(0);
const clusters = ref([]);

const accountId = computed(() => Number(route.params.accountId));
const hasDateRange = computed(() => props.filters?.from && props.filters?.to);

const conversationLink = conversationId => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      id: conversationId,
    })
  );
};

const fetchData = async () => {
  if (!hasDateRange.value) return;
  isLoading.value = true;
  errorText.value = '';
  try {
    const response = await ReportsAPI.getBotUnresolvedQuestions({
      from: props.filters.from,
      to: props.filters.to,
      limit: 30,
      samplePerCluster: 3,
    });
    totalHandoffConversations.value =
      response.data.total_handoff_conversations || 0;
    clusters.value = response.data.clusters || [];
  } catch (e) {
    errorText.value = e?.message || 'Failed to load unresolved questions';
  } finally {
    isLoading.value = false;
  }
};

const createImproveItem = async row => {
  try {
    await ReportsAPI.createBotImproveItem({
      normalized: row.normalized,
      example: row.example,
      sampleConversationIds: row.sample_conversation_ids || [],
      sampleConversationDisplayIds: row.sample_conversation_display_ids || [],
    });
    useAlert('Created improve item');
  } catch (e) {
    useAlert(e?.message || 'Failed to create improve item');
  }
};

watch(() => props.filters, fetchData, { deep: true });
onMounted(fetchData);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      class="mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <div class="flex items-start justify-between gap-4">
        <div class="flex flex-col gap-1">
          <div class="text-base font-semibold text-slate-800 dark:text-white">
            {{ $t('BOT_REPORTS.UNRESOLVED.HEADER') }}
          </div>
          <div class="text-sm text-slate-600 dark:text-white/60">
            {{ $t('BOT_REPORTS.UNRESOLVED.DESCRIPTION') }}
          </div>
        </div>
        <div class="text-sm text-slate-600 dark:text-white/60">
          <span v-if="hasDateRange">
            {{
              $t('BOT_REPORTS.UNRESOLVED.TOTAL_HANDOFF', {
                count: totalHandoffConversations,
              })
            }}
          </span>
        </div>
      </div>

      <div
        v-if="!hasDateRange"
        class="mt-4 text-sm text-slate-600 dark:text-white/60"
      >
        {{ $t('BOT_REPORTS.UNRESOLVED.EMPTY_STATE') }}
      </div>

      <div v-else class="mt-4">
        <div v-if="isLoading" class="text-sm text-slate-600 dark:text-white/60">
          {{ $t('BOT_REPORTS.UNRESOLVED.LOADING') }}
        </div>
        <div v-else-if="errorText" class="text-sm text-red-600">
          {{ errorText }}
        </div>
        <div
          v-else-if="!clusters.length"
          class="text-sm text-slate-600 dark:text-white/60"
        >
          {{ $t('BOT_REPORTS.UNRESOLVED.NO_DATA') }}
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm mt-2">
            <thead>
              <tr class="text-left text-slate-600 dark:text-white/60">
                <th class="py-2 pr-4 font-medium">
                  {{ $t('BOT_REPORTS.UNRESOLVED.TABLE.TOPIC') }}
                </th>
                <th class="py-2 pr-4 font-medium w-24">
                  {{ $t('BOT_REPORTS.UNRESOLVED.TABLE.COUNT') }}
                </th>
                <th class="py-2 pr-4 font-medium w-56">
                  {{ $t('BOT_REPORTS.UNRESOLVED.TABLE.SAMPLES') }}
                </th>
                <th class="py-2 pr-4 font-medium w-24" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in clusters"
                :key="row.normalized"
                class="border-t border-white/5"
              >
                <td class="py-3 pr-4">
                  <div
                    class="text-slate-800 dark:text-white whitespace-pre-wrap"
                  >
                    {{ row.example || row.normalized }}
                  </div>
                  <div class="text-xs text-slate-600 dark:text-white/60 mt-1">
                    {{ row.normalized }}
                  </div>
                </td>
                <td class="py-3 pr-4 text-slate-800 dark:text-white">
                  {{ row.count }}
                </td>
                <td class="py-3 pr-4">
                  <div class="flex flex-wrap gap-2">
                    <a
                      v-for="(
                        cid, index
                      ) in row.sample_conversation_display_ids ||
                      row.sample_conversation_ids ||
                      []"
                      :key="`${cid}-${index}`"
                      class="text-n-brand underline"
                      :href="conversationLink(cid)"
                      target="_blank"
                      rel="noreferrer"
                    >
                      #{{ cid }}
                    </a>
                  </div>
                </td>
                <td class="py-3 pr-4">
                  <button
                    class="secondary-action-btn"
                    @click="createImproveItem(row)"
                  >
                    {{ $t('BOT_REPORTS.UNRESOLVED.TABLE.IMPROVE') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.secondary-action-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 9999px;
  cursor: pointer;
}

.secondary-action-btn:hover {
  background: rgba(255, 255, 255, 0.06);
}
</style>
